/**
 * @swoft/ddd - MongoDB Collection Configuration
 * 
 * Centralized definition of all collection names used by the DDD bounded contexts.
 * Leverages the @swoft/mongo package for database connection management.
 * 
 * Following Eric Evans Domain-Driven Design principles with clean separation of:
 * - Strategic Design (domains, bounded contexts)
 * - Tactical Design (aggregates, entities, value objects)
 * - Meta-Model Design (package mappings, analysis)
 */

import { getMongoDb } from "@swoft/persistence";
import type { Collection, Document } from "mongodb";

/**
 * Default project ID used across the DDD domain
 * Shared between seeders and repositories for consistency
 */
export const DEFAULT_DDD_PROJECT_ID = 'c3f0fb44-79da-4200-b6c8-bd30ab57a6db';

/**
 * MongoDB Collection Names for DDD Package
 * 
 * Standardized naming convention: ddd_{context}_{entity}
 * Maintains backward compatibility with existing collection names
 */
export const DDD_COLLECTIONS = {
  // Strategic Design Bounded Context
  DOMAINS: 'ddd_domains',
  BOUNDED_CONTEXTS: 'ddd_bounded_contexts',
  
  // Tactical Design Bounded Context
  AGGREGATES: 'ddd_aggregates',
  
  // Meta-Model Design Bounded Context
  PACKAGE_MAPPINGS: 'ddd_package_mappings'
} as const;

/**
 * Type-safe collection name type
 */
export type DDDCollectionName = typeof DDD_COLLECTIONS[keyof typeof DDD_COLLECTIONS];

/**
 * Get a MongoDB collection using the centralized @swoft/mongo package
 * 
 * @param collectionName - The collection constant from DDD_COLLECTIONS
 * @returns MongoDB Collection instance
 */
export function getDDDCollection<T extends Document = Document>(collectionName: DDDCollectionName): Collection<T> {
  const db = getMongoDb(); // Uses existing @swoft/mongo package
  return db.collection<T>(collectionName);
}

/**
 * Collection metadata with bounded context mapping
 * Following Eric Evans DDD principles and Clean Architecture
 */
export const DDD_COLLECTION_METADATA = {
  [DDD_COLLECTIONS.DOMAINS]: {
    boundedContext: 'strategic-design',
    description: 'Business domains representing core business areas and capabilities',
    primaryKey: 'domainId',
    dddLayer: 'strategic',
    aggregateRoot: 'Domain'
  },
  [DDD_COLLECTIONS.BOUNDED_CONTEXTS]: {
    boundedContext: 'strategic-design',
    description: 'Bounded contexts defining clear boundaries for business capabilities',
    primaryKey: 'boundedContextId',
    dddLayer: 'strategic',
    aggregateRoot: 'BoundedContext'
  },
  [DDD_COLLECTIONS.AGGREGATES]: {
    boundedContext: 'tactical-design',
    description: 'Aggregates as consistency boundaries and main building blocks',
    primaryKey: 'aggregateId',
    dddLayer: 'tactical',
    aggregateRoot: 'Aggregate'
  },
  [DDD_COLLECTIONS.PACKAGE_MAPPINGS]: {
    boundedContext: 'meta-model-design',
    description: 'Package-to-domain mappings for monorepo analysis and optimization',
    primaryKey: 'mappingId',
    dddLayer: 'implementation',
    aggregateRoot: 'PackageMapping'
  }
} as const;

/**
 * Package Information (following @swoft/dev-ops pattern)
 */
export const DDD_PACKAGE_INFO = {
  name: '@swoft/ddd',
  version: '0.1.0',
  description: 'Domain-Driven Design strategic and tactical design implementation',
  architecture: 'Clean Architecture + Domain-Driven Design',
  methodology: 'Eric Evans Domain-Driven Design',
  boundedContexts: [
    'Strategic Design',
    'Tactical Design',
    'Meta-Model Design'
  ],
  dddLayers: [
    'Strategic Design (Domains, Bounded Contexts)',
    'Tactical Design (Aggregates, Entities, Value Objects)', 
    'Meta-Model Design (Package Mappings, Analysis)'
  ],
  collections: Object.values(DDD_COLLECTIONS),
  databaseDependency: '@swoft/mongo'
} as const;

/**
 * Collection Migration Guide
 * 
 * CONSISTENT NAMING (no changes needed from existing implementation):
 * 'ddd_domains' ✓ (already correct)
 * 'ddd_boundedcontexts' → 'ddd_bounded_contexts' (minor consistency improvement)
 * 'ddd_aggregates' ✓ (already correct)
 * 'ddd_package_mappings' ✓ (already correct, but was hardcoded as 'package_mappings')
 */
export const DDD_COLLECTION_MIGRATION_GUIDE = {
  migrations: [
    {
      from: 'ddd_boundedcontexts',
      to: DDD_COLLECTIONS.BOUNDED_CONTEXTS,
      reason: 'Consistent underscore naming convention (bounded_contexts)',
      affectedFiles: [
        'MongoBoundedContextRepository.ts'
      ]
    }
  ],
  noChangesNeeded: [
    {
      collection: DDD_COLLECTIONS.DOMAINS,
      reason: 'Already follows correct naming convention',
      files: ['MongoDomainRepository.ts']
    },
    {
      collection: DDD_COLLECTIONS.AGGREGATES,
      reason: 'Already follows correct naming convention', 
      files: ['MongoAggregateRepository.ts']
    },
    {
      collection: DDD_COLLECTIONS.PACKAGE_MAPPINGS,
      reason: 'Correctly scoped with ddd_ prefix',
      files: ['MongoPackageMappingRepository.ts']
    }
  ]
} as const;